/**
 * Chat API Client
 *
 * Provides streaming chat interface for HiveCore
 * Uses HTTP NDJSON streaming to /api/chat endpoint
 * Trigger rebuild for Netlify env vars
 *
 * Auth: Uses X-HiveCore-Internal-Token header when VITE_HIVECORE_INTERNAL_TOKEN is set (staging).
 * Falls back to JWT Bearer token for production.
 */

import { getStudioApiBase } from '../studio/api';
import type { ChatMessage } from '../stores/useChatStore';

/**
 * Get the API base URL for chat endpoints.
 * Uses VITE_BACKEND_URL if set (staging/production), otherwise falls back to localhost for local dev.
 */
function getChatApiBase(): string {
    // Use studioFetch's base URL logic for consistency
    const studioBase = getStudioApiBase();
    if (studioBase) {
        return studioBase;
    }
    // Fallback for local development only
    return 'http://localhost:3000';
}

export interface ChatSegmentAction {
    id: string;
    label: string;
    payload?: Record<string, unknown>;
}

export interface ChatSegment {
    type: 'text' | 'report' | 'task' | 'approval' | 'notice' | 'token' | 'done' | 'error';
    text?: string;
    /** Individual token for streaming (type: 'token') */
    token?: string;
    /** Final assistant message (type: 'done') */
    assistantMessage?: RestChatMessage;
    /** Error message (type: 'error') */
    error?: string;
    /** Provider used (type: 'done') */
    provider?: string;
    /** Model used (type: 'done') */
    model?: string;
    /** Execution envelope from SOMA spine (type: 'done', constitutional compliance) */
    execution?: ChatExecutionEnvelope;
    /**
     * CQC-F-022: Full content before truncation (type: 'done')
     * When presentation enforcement truncates content, the full original
     * content is preserved here for copy operations. UI must use this
     * as the canonical source for clipboard operations.
     */
    fullContent?: string;
    /**
     * Optional metadata for assistant message rendering (e.g. artifact redirect).
     * This is only present for special frames that are not standard ChatStreamResponse segments.
     */
    meta?: {
        artifactRedirect?: boolean;
        artifactId?: string;
        artifactTitle?: string;
        artifactMime?: string;
        artifactSizeBytes?: number;
        reason?: string;
    };
    report?: unknown;
    task?: unknown;
    approval?: unknown;
    notice?: unknown;
    actions?: ChatSegmentAction[];
    artifacts?: string[];
    lineage?: string[];
}

export interface ChatStreamResponse {
    session_id: string;
    segment: ChatSegment;
}

interface ArtifactRedirectAssistantFrame {
    role: 'assistant';
    content: string;
    meta?: {
        artifactRedirect?: boolean;
        artifactId?: string;
        artifactTitle?: string;
        artifactMime?: string;
        artifactSizeBytes?: number;
        reason?: string;
    };
}

function isArtifactRedirectAssistantFrame(value: unknown): value is ArtifactRedirectAssistantFrame {
    if (!value || typeof value !== 'object') return false;
    const v = value as Partial<ArtifactRedirectAssistantFrame>;
    return (
        v.role === 'assistant' &&
        typeof v.content === 'string' &&
        !!v.meta &&
        (v.meta as any).artifactRedirect === true
    );
}

class ChatAPI {
    /**
     * Stream chat response using NDJSON
     * @param message - User message
     * @param sessionId - Chat session ID
     * @returns AsyncGenerator of chat segments
     */
    private getAuthHeaders(): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env['VITE_HIVECORE_JWT'] || 'DEV_JWT_FOR_TESTING'}`,
        };

        // Add internal token for staging/dev if configured
        const internalToken = import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN'];
        if (internalToken) {
            headers['X-HiveCore-Internal-Token'] = internalToken;
        }

        return headers;
    }

    async *streamChatResponse(
        message: string,
        sessionId: string
    ): AsyncGenerator<ChatSegment, void, unknown> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 180000); // 180s (3 min) timeout for streaming

        try {
            const response = await fetch(`${getChatApiBase()}/api/chat`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    message,
                    session_id: sessionId,
                }),
                signal: controller.signal,
                credentials: 'include',
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch {
                    console.debug('Failed to parse error response JSON');
                }
                throw new Error(errorMessage);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(trimmed) as unknown;
                        const maybe = parsed as Partial<ChatStreamResponse>;
                        if (maybe.segment) {
                            yield maybe.segment;
                            continue;
                        }
                        // Artifact redirect finalization frame (new, canonical).
                        if (isArtifactRedirectAssistantFrame(parsed)) {
                            yield { type: 'text', text: parsed.content, meta: parsed.meta };
                        }
                    } catch (error) {
                        console.error('Failed to parse NDJSON line:', trimmed, error);
                    }
                }
            }

            if (buffer.trim()) {
                try {
                    const parsed = JSON.parse(buffer.trim()) as unknown;
                    const maybe = parsed as Partial<ChatStreamResponse>;
                    if (maybe.segment) {
                        yield maybe.segment;
                    } else if (isArtifactRedirectAssistantFrame(parsed)) {
                        yield { type: 'text', text: parsed.content, meta: parsed.meta };
                    }
                } catch (error) {
                    console.error('Failed to parse final NDJSON line:', buffer, error);
                }
            }
        } catch (error) {
            clearTimeout(timeoutId);
            console.error('Chat stream error:', error);

            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Network error: Unable to connect to chat server');
            }

            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Chat request timeout: Server took too long to respond');
            }

            throw error;
        }
    }
}

/**
 * ============================================================================
 * NEW REST API CLIENT (chat_core Phase 3)
 * ============================================================================
 */

export interface RestChatSession {
    id: string;
    missionId?: string;
    createdAt: string;
    updatedAt: string;
    title?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Execution envelope from SOMA spine (constitutional compliance).
 * Contains verdict, artifacts, and reasonCode for proper UI rendering.
 */
export interface ChatExecutionEnvelope {
    mode?: 'answer_only' | 'execution_required';
    executed?: boolean;
    /** Step 7: plan-only mode flag (mode remains execution_required, executed=false) */
    planOnly?: boolean;
    jobId?: string | null;
    executionId?: string | null;
    verdict?: 'SUCCESS' | 'FAILED' | 'INCOMPLETE' | 'BLOCKED' | 'DENIED' | 'NOT_APPLICABLE';
    artifacts?: {
        plan?: string;
        trace?: string;
        verification?: string;
        ledger?: string;
    };
    reasonCode?: string;
    intentName?: string;
    intentClass?: string;
    nextActions?: Array<{
        label: string;
        type: 'button' | 'link' | 'suggestion';
        payload: string;
    }>;
    verified?: boolean;
}

export interface RestChatMessage {
    id: string;
    sessionId: string;
    role: 'system' | 'user' | 'assistant' | 'tool';
    content: string;
    name?: string;
    toolName?: string;
    toolArgs?: Record<string, unknown>;
    createdAt: string;
    /**
     * Message type for card rendering.
     * If provided by backend, this determines how the message is rendered.
     * If not provided, mapBackendMessageToUi will default to 'text' (no heuristics).
     */
    type?: 'text' | 'code' | 'pluginCard' | 'taskResult' | 'assistant-log' | 'soma-response';
    /**
     * Optional metadata for rich message rendering (confidence, model, etc.)
     */
    metadata?: {
        model?: string;
        confidence?: number;
        explanation?: string;
        threadId?: string;
        /** Execution envelope for verdict-based rendering (constitutional compliance) */
        executionEnvelope?: ChatExecutionEnvelope;
        /** @deprecated Use executionEnvelope.verdict instead */
        verified?: boolean;
    };
}

export interface CreateSessionResponse {
    success: boolean;
    sessionId: string;
    missionId?: string;
    createdAt: string;
    updatedAt: string;
    title?: string;
}

export interface GetSessionResponse {
    success: boolean;
    session: RestChatSession;
    messages: RestChatMessage[];
    /** Cursor for loading older messages (only present when using pagination) */
    nextCursor?: string;
    /** Whether there are more older messages to load */
    hasMore?: boolean;
}

export interface GetSessionParams {
    /** Max messages to return (default 50, max 100) */
    limit?: number;
    /** Message ID cursor for loading older messages */
    cursor?: string;
}

export interface SendMessageResponse {
    success: boolean;
    session: RestChatSession;
    messages: RestChatMessage[];
    llmResponse?: RestChatMessage;
    /** Execution envelope from SOMA spine (constitutional compliance) */
    execution?: ChatExecutionEnvelope;
}

export interface ListSessionsResponse {
    success: boolean;
    sessions: Array<RestChatSession & { messageCount?: number }>;
    nextCursor?: string;
    hasMore?: boolean;
}

export interface ListSessionsParams {
    query?: string;
    missionId?: string;
    limit?: number;
    cursor?: string;
}

/**
 * Presentation contract (chat surface):
 * - Markdown/code must be emitted as artifacts only (never rendered as chat text).
 * - Reject markdown-as-chat (headings, fenced code).
 */
function containsDisallowedMarkdownForChat(content: string): boolean {
    // Disallow fenced code blocks and headings.
    // Allow inline triple backticks in prose and inline backticks as plain text.
    const lines = content.split(/\r?\n/);
    let hasFence = false;
    for (let i = 0; i < lines.length; i++) {
        if (/^```[A-Za-z0-9_-]*\s*$/.test(lines[i])) {
            for (let j = i + 1; j < lines.length; j++) {
                if (/^```\s*$/.test(lines[j])) {
                    hasFence = true;
                    break;
                }
            }
        }
        if (hasFence) break;
    }
    if (hasFence) return true;
    return /^#{1,6}\s+/m.test(content);
}

/**
 * Map backend ChatMessage to UI ChatMessage format.
 *
 * IMPORTANT: This function now preserves the message type from backend if provided,
 * or infers it from content patterns. This enables color card rendering for
 * pluginCard, taskResult, code, and soma-response message types.
 *
 * Previously, this function hardcoded type: 'text' which prevented card rendering.
 * See: Phase 1 fix for color cards telemetry/bee feedback.
 */
function mapBackendMessageToUi(msg: RestChatMessage): ChatMessage {
    let sender: ChatMessage['sender'];
    switch (msg.role) {
        case 'assistant':
            sender = 'assistant';
            break;
        case 'user':
            sender = 'user';
            break;
        case 'system':
            sender = 'system';
            break;
        case 'tool':
            sender = 'system'; // Map tool messages to system for now
            break;
        default:
            sender = 'system';
    }

    // Enforce: no heuristic UI modes.
    // If backend doesn't provide type, default to 'text'.
    let messageType: ChatMessage['type'] = msg.type || 'text';

    // Presentation contract safety net (UI-side):
    // If an assistant message is plain text but contains disallowed markdown/code, reject it.
    if (
        msg.role === 'assistant' &&
        messageType === 'text' &&
        containsDisallowedMarkdownForChat(msg.content)
    ) {
        messageType = 'pluginCard';
        const violationCard = {
            title: 'Presentation Contract Violation',
            description:
                'Assistant output contained markdown/code and was rejected for chat rendering. It must be emitted as artifacts only.',
            status: 'failed',
            data: {
                rule: 'markdown/code → artifacts only',
                rejectedPreview: msg.content.slice(0, 200),
            },
            actions: [],
        };

        return {
            id: msg.id,
            sender,
            content: JSON.stringify(violationCard),
            type: messageType,
            messageType: 'chat',
            timestamp: msg.createdAt,
            buildCredits: 0,
            sessionId: msg.sessionId,
            ...(msg.metadata && { metadata: msg.metadata }),
        };
    }

    // Tool messages can explicitly request card rendering without content-based inference.
    if (msg.role === 'tool' && !msg.type && msg.toolName === 'artifact') {
        messageType = 'pluginCard';
    }

    return {
        id: msg.id,
        sender,
        content: msg.content,
        type: messageType,
        messageType: 'chat',
        timestamp: msg.createdAt,
        buildCredits: Math.ceil(msg.content.length / 4),
        sessionId: msg.sessionId,
        // Preserve metadata if provided (for soma-response confidence display, etc.)
        ...(msg.metadata && { metadata: msg.metadata }),
    };
}

/**
 * Map array of backend messages to UI format
 */
function mapBackendHistoryToUi(messages: RestChatMessage[]): ChatMessage[] {
    return messages.map(mapBackendMessageToUi);
}

/**
 * REST API client for chat operations
 */
class RestChatAPI {
    private getAuthHeaders(): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env['VITE_HIVECORE_JWT'] || 'DEV_JWT_FOR_TESTING'}`,
        };

        // Add internal token for staging/dev if configured
        const internalToken = import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN'];
        if (internalToken) {
            headers['X-HiveCore-Internal-Token'] = internalToken;
        }

        return headers;
    }

    /**
     * Create a new chat session
     */
    async createChatSession(params?: {
        missionId?: string;
        title?: string;
    }): Promise<CreateSessionResponse> {
        const response = await fetch(`${getChatApiBase()}/api/chat/sessions`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(params || {}),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Get session and message history with optional pagination.
     *
     * @param sessionId - Session ID
     * @param params.limit - Max messages to return (default 50, max 100)
     * @param params.cursor - Message ID cursor for loading older messages
     */
    async getChatSession(
        sessionId: string,
        params?: GetSessionParams
    ): Promise<GetSessionResponse> {
        const searchParams = new URLSearchParams();

        if (params?.limit) {
            searchParams.set('limit', params.limit.toString());
        }
        if (params?.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const queryString = searchParams.toString();
        const url = `${getChatApiBase()}/api/chat/sessions/${sessionId}${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Send a message and get response
     */
    async sendChatMessage(sessionId: string, content: string): Promise<SendMessageResponse> {
        const response = await fetch(
            `${getChatApiBase()}/api/chat/sessions/${sessionId}/messages`,
            {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ content }),
                credentials: 'include',
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Send a message and stream the response using NDJSON
     * This combines REST session management with streaming responses.
     * Tokens are streamed as they arrive instead of waiting for the full response.
     *
     * Stream format from server:
     * - { type: 'token', session_id, token } - Individual tokens for smooth rendering
     * - { type: 'segment', session_id, segment } - Operational segments (reports, tasks, etc.)
     * - { type: 'done', session_id, assistantMessage, provider, model } - Stream complete
     * - { type: 'error', session_id, error } - Error during streaming
     *
     * @param sessionId - Session ID
     * @param content - Message content
     * @returns AsyncGenerator of chat segments including tokens
     */
    async *streamChatMessage(
        sessionId: string,
        content: string
    ): AsyncGenerator<ChatSegment, void, unknown> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 180000); // 180s (3 min) timeout for streaming

        try {
            const response = await fetch(
                `${getChatApiBase()}/api/chat/sessions/${sessionId}/messages/stream`,
                {
                    method: 'POST',
                    headers: this.getAuthHeaders(),
                    body: JSON.stringify({ content }),
                    signal: controller.signal,
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.error || `HTTP ${response.status}: ${response.statusText}`
                );
            }

            if (!response.body) {
                throw new Error('No response body for streaming');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(trimmed) as Record<string, unknown>;

                        // Handle new streaming format with type field
                        if (parsed['type'] === 'token' && typeof parsed['token'] === 'string') {
                            // Yield individual token for progressive rendering
                            yield { type: 'token', token: parsed['token'] };
                            continue;
                        }

                        if (parsed['type'] === 'done') {
                            // Stream complete - yield done with final message info
                            yield {
                                type: 'done',
                                assistantMessage: parsed['assistantMessage'] as
                                    | RestChatMessage
                                    | undefined,
                                provider: parsed['provider'] as string | undefined,
                                model: parsed['model'] as string | undefined,
                            };
                            continue;
                        }

                        if (parsed['type'] === 'error') {
                            // Error during streaming
                            yield { type: 'error', error: parsed['error'] as string };
                            continue;
                        }

                        if (parsed['type'] === 'segment' && parsed['segment']) {
                            // Operational segment (report, task, etc.)
                            yield parsed['segment'] as ChatSegment;
                            continue;
                        }

                        // Legacy format: { session_id, segment }
                        const maybe = parsed as Partial<ChatStreamResponse>;
                        if (maybe.segment) {
                            yield maybe.segment;
                            continue;
                        }

                        // Legacy artifact redirect format
                        if (isArtifactRedirectAssistantFrame(parsed)) {
                            yield {
                                type: 'text',
                                text: parsed.content as string,
                                meta: parsed.meta as ChatSegment['meta'],
                            };
                        }
                    } catch {
                        console.warn('Failed to parse streaming line:', trimmed);
                    }
                }
            }

            // Process any remaining buffer
            if (buffer.trim()) {
                try {
                    const parsed = JSON.parse(buffer.trim()) as Record<string, unknown>;

                    if (parsed['type'] === 'token' && typeof parsed['token'] === 'string') {
                        yield { type: 'token', token: parsed['token'] };
                    } else if (parsed['type'] === 'done') {
                        yield {
                            type: 'done',
                            assistantMessage: parsed['assistantMessage'] as
                                | RestChatMessage
                                | undefined,
                            provider: parsed['provider'] as string | undefined,
                            model: parsed['model'] as string | undefined,
                        };
                    } else if (parsed['type'] === 'segment' && parsed['segment']) {
                        yield parsed['segment'] as ChatSegment;
                    } else {
                        const maybe = parsed as Partial<ChatStreamResponse>;
                        if (maybe.segment) {
                            yield maybe.segment;
                        } else if (isArtifactRedirectAssistantFrame(parsed)) {
                            yield {
                                type: 'text',
                                text: parsed.content as string,
                                meta: parsed.meta as ChatSegment['meta'],
                            };
                        }
                    }
                } catch {
                    console.warn('Failed to parse final buffer:', buffer);
                }
            }
        } catch (error) {
            clearTimeout(timeoutId);
            console.error('REST chat stream error:', error);

            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Network error: Unable to connect to chat server');
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Update session title.
     * Used for Phase D auto-naming after 2-4 messages.
     *
     * @param sessionId - Session ID
     * @param title - New title for the session (max 100 characters)
     */
    async updateSessionTitle(
        sessionId: string,
        title: string
    ): Promise<{ success: boolean; session: RestChatSession }> {
        const response = await fetch(`${getChatApiBase()}/api/chat/sessions/${sessionId}`, {
            method: 'PATCH',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ title }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Find sessions by missionId (legacy - use listSessions instead)
     */
    async getChatSessionByMission(missionId: string): Promise<ListSessionsResponse> {
        return this.listSessions({ missionId });
    }

    /**
     * List sessions with optional filtering and cursor-based pagination.
     * This is the primary method for fetching sessions for the Sessions Drawer.
     *
     * @param params.query - Search by title (optional)
     * @param params.missionId - Filter by mission (optional)
     * @param params.limit - Max results (default 50, max 100)
     * @param params.cursor - Pagination cursor for loading more
     */
    async listSessions(params?: ListSessionsParams): Promise<ListSessionsResponse> {
        const searchParams = new URLSearchParams();

        if (params?.query) {
            searchParams.set('query', params.query);
        }
        if (params?.missionId) {
            searchParams.set('missionId', params.missionId);
        }
        if (params?.limit) {
            searchParams.set('limit', params.limit.toString());
        }
        if (params?.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const queryString = searchParams.toString();
        const url = `${getChatApiBase()}/api/chat/sessions${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }
}

export const restChatAPI = new RestChatAPI();
export { mapBackendMessageToUi, mapBackendHistoryToUi };

export const chatAPI = new ChatAPI();
export default chatAPI;

/**
 * ============================================================================
 * CHAT ACTION API (ChatEvoControlBridge v1)
 * ============================================================================
 */

export type ChatActionType = 'evo_observe_run' | 'evo_build_run';

export interface IntentClassification {
    kind: 'chat' | 'action';
    actionType?: ChatActionType;
    confidence: number;
    requiresConfirm: boolean;
    reason: string;
}

export interface ActionPreviewResponse {
    intent: IntentClassification;
    proposedAction?: {
        actionType: ChatActionType;
        description: string;
        dryRun: boolean;
        estimatedSteps: string[];
    };
}

export interface ActionExecuteResponse {
    success: boolean;
    signalId?: string;
    problemId?: string;
    specId?: string;
    runId?: string;
    dryRun: boolean;
    gateResult?: {
        pass: boolean;
        reasons: string[];
    };
    timeline: Array<{
        event: string;
        timestamp: string;
        details?: string;
    }>;
    error?: string;
}

export interface ActionConfirmResponse {
    success: boolean;
    prUrl?: string;
    gateResult?: {
        pass: boolean;
        reasons: string[];
    };
    mergeEligible: boolean;
    error?: string;
}

/**
 * Chat Action API client for EvoControl bridge
 */
class ChatActionAPI {
    private getAuthHeaders(): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env['VITE_HIVECORE_JWT'] || 'DEV_JWT_FOR_TESTING'}`,
        };

        // Add internal token for staging/dev if configured
        const internalToken = import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN'];
        if (internalToken) {
            headers['X-HiveCore-Internal-Token'] = internalToken;
        }

        return headers;
    }

    /**
     * Preview intent classification without execution
     */
    async previewAction(
        message: string,
        context?: { env?: string; repo?: string }
    ): Promise<ActionPreviewResponse> {
        const response = await fetch(`${getChatApiBase()}/api/studio/chat/actions/preview`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ message, context }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Execute an action (observe mode by default)
     */
    async executeAction(
        message: string,
        actionType: ChatActionType,
        context?: { env?: string; repo?: string }
    ): Promise<ActionExecuteResponse> {
        const response = await fetch(`${getChatApiBase()}/api/studio/chat/actions/execute`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ message, actionType, context }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Confirm build mode execution after successful observe run
     */
    async confirmBuildRun(
        runId: string,
        confirmId: string,
        specId: string
    ): Promise<ActionConfirmResponse> {
        const response = await fetch(`${getChatApiBase()}/api/studio/chat/actions/confirm`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ runId, confirmId, specId }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }
}

export const chatActionAPI = new ChatActionAPI();
